import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Arrow({ x1, y1, x2, y2, color, width, dash, id }) {
  const markerId = "arr_" + (id || Math.round(x1+y1+x2+y2));
  return (
    <g>
      <defs>
        <marker id={markerId} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <polygon points="0 0,7 3.5,0 7" fill={color || "#2E75B6"}/>
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={color || "#2E75B6"} strokeWidth={width || 2.4}
        strokeDasharray={dash || ""} markerEnd={"url(#"+markerId+")"}/>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Vector Anatomy
// ─────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W = 900, H = 360;
  const ox = 69, oy = 200, sc = 58;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;
  const vx = 3, vy = 2;
  const mag = Math.sqrt(vx*vx + vy*vy);
  const ang = Math.atan2(vy, vx) * 180 / Math.PI;
  const px0 = 430;
  const panW = W - px0 - 8;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Vector Anatomy — Magnitude, Direction, Components, and Notation</div>
      <div style={{ textAlign:'center' }}>
        <svg viewBox={"0 0 "+W+" "+H} style={{ width:'100%', display:'block' }}>
          {[0,1,2,3].map(v => (
            <g key={v}>
              <line x1={toX(v)} y1={14} x2={toX(v)} y2={oy+8} stroke="#eee" strokeWidth="1"/>
              <line x1={ox-8} y1={toY(v)} x2={toX(3.3)} y2={toY(v)} stroke="#eee" strokeWidth="1"/>
              <text x={toX(v)} y={oy+18} textAnchor="middle" fontSize="14" fill="#bbb">{v}</text>
              {v>0 && <text x={ox-4} y={toY(v)+3} textAnchor="end" fontSize="14" fill="#bbb">{v}</text>}
            </g>
          ))}
          <line x1={ox-8} y1={oy} x2={toX(3.3)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox} y1={oy+8} x2={ox} y2={14} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={toX(3.3)+4} y={oy+4} fontSize="16" fill="#1F3864" fontWeight="700">x</text>
          <text x={ox+3} y={12} fontSize="16" fill="#1F3864" fontWeight="700">y</text>

          <line x1={toX(0)} y1={oy} x2={toX(vx)} y2={oy} stroke="#2E75B6" strokeWidth="2" strokeDasharray="5,3"/>
          <text x={toX(vx/2)} y={oy+28} textAnchor="middle" fontSize="15" fill="#2E75B6" fontWeight="700">vx = {vx}</text>

          <line x1={toX(vx)} y1={oy} x2={toX(vx)} y2={toY(vy)} stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3"/>
          <text x={toX(vx)+13} y={toY(vy/2)+4} fontSize="15" fill="#c0392b" fontWeight="700">vy = {vy}</text>

          <polyline points={toX(vx-0.14)+","+oy+" "+toX(vx-0.14)+","+(oy-sc*0.14)+" "+toX(vx)+","+(oy-sc*0.14)}
            fill="none" stroke="#888" strokeWidth="1.2"/>

          <Arrow x1={toX(0)} y1={oy} x2={toX(vx)-4} y2={toY(vy)+3} color="#1F3864" width={3} id="v_main"/>
          <text x={toX(vx)-30} y={toY(vy)-10} fontSize="16" fill="#1F3864" fontWeight="700">v</text>

          <circle cx={toX(0)} cy={oy} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
          <text x={toX(0)} y={oy+28} textAnchor="middle" fontSize="15" fill="#27ae60" fontWeight="700">tail</text>

          <circle cx={toX(vx)} cy={toY(vy)} r={5} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
          <text x={toX(vx)+10} y={toY(vy)+5} fontSize="15" fill="#e07b00" fontWeight="700">head</text>

          {(() => {
            const r = 48, t = Math.atan2(vy, vx);
            return (
              <g>
                <path d={"M "+(ox+r)+","+oy+" A "+r+" "+r+" 0 0 0 "+(ox+r*Math.cos(t))+","+(oy-r*Math.sin(t))}
                  fill="none" stroke="#e07b00" strokeWidth="1.8"/>
                <text x={ox+52} y={oy-16} fontSize="15" fill="#e07b00" fontWeight="700">{"θ="+ang.toFixed(1)+"°"}</text>
              </g>
            );
          })()}

          {/* ── RIGHT: info panel — 26px per row, no overlap ── */}
          {/* 7 rows × 26px = 182px + title 32px = 214px outer box */}
          <rect x={px0} y={10} width={panW} height={218} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={px0+panW/2} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">
            {"Vector v = ("+vx+", "+vy+")"}
          </text>
          {[
            ["Component form:", "v = "+vx+"i + "+vy+"j", "#1F3864"],
            ["Magnitude |v|:", "|v| = sqrt(vx² + vy²)", "#27ae60"],
            ["Calculated:", "|v| = "+mag.toFixed(3), "#27ae60"],
            ["Direction θ:", "θ = arctan(vy / vx)", "#e07b00"],
            ["Calculated:", "θ = "+ang.toFixed(1)+"°", "#e07b00"],
            ["Unit vector v̂:", "v̂ = v / |v|", "#2E75B6"],
            ["Calculated:", "("+( vx/mag).toFixed(3)+", "+(vy/mag).toFixed(3)+")", "#2E75B6"],
          ].map(([lbl, val, col], i) => (
            <g key={i}>
              <text x={px0+12} y={44+i*26} fontSize="12" fill="#999">{lbl}</text>
              <text x={px0+12} y={58+i*26} fontSize="14" fill={col} fontFamily="'Courier New',monospace" fontWeight="700">{val}</text>
            </g>
          ))}

          {/* Scalar vs vector box — starts at y=234, height=82 → bottom=316 < H=360 ✓ */}
          <rect x={px0} y={234} width={panW} height={82} rx={4} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
          <text x={px0+panW/2} y={251} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Scalar vs Vector</text>
          <text x={px0+12} y={269} fontSize="13" fill="#555" fontFamily="'Courier New',monospace">Scalar: magnitude only  (speed, temp, mass)</text>
          <text x={px0+12} y={287} fontSize="13" fill="#555" fontFamily="'Courier New',monospace">Vector: magnitude + direction  (velocity, force)</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        A vector is an arrow with a tail (start) and a head (end). Its two essential properties are magnitude (length) and direction (angle). Any 2D vector decomposes into orthogonal x and y components using the Pythagorean theorem.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Vector Addition and Subtraction
// ─────────────────────────────────────────────────────────────────
function DiagramAddSub() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Vector Addition and Subtraction — Tip-to-Tail, Parallelogram, and Geometric Subtraction</div>
      <div style={{ textAlign:'center' }}>
        <svg viewBox={"0 0 "+W+" "+H} style={{ width:'100%', display:'block' }}>

          {/* ── Panel 1: Tip-to-tail addition ── */}
          <rect x={17} y={10} width={273} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={154} y={28} textAnchor="middle" fontSize="17" fill="#1F3864" fontWeight="700">Tip-to-Tail Addition</text>
          <text x={154} y={44} textAnchor="middle" fontSize="16" fill="#888">a + b = R</text>
          {(() => {
            const ox=62, oy=160;
            const ax=130, ay=-28, bx=66, by=-72;
            const rx=ax+bx, ry=ay+by;
            return (
              <g>
                <Arrow x1={ox} y1={oy} x2={ox+ax-4} y2={oy+ay+2} color="#2E75B6" id="tt_a"/>
                <text x={ox+ax/2-16} y={oy+ay/2-6} fontSize="17" fill="#2E75B6" fontWeight="700">a</text>
                <Arrow x1={ox+ax} y1={oy+ay} x2={ox+ax+bx-3} y2={oy+ay+by+4} color="#27ae60" id="tt_b"/>
                <text x={ox+ax+bx/2+6} y={oy+ay+by/2+4} fontSize="17" fill="#27ae60" fontWeight="700">b</text>
                <Arrow x1={ox} y1={oy} x2={ox+rx-4} y2={oy+ry+4} color="#c0392b" width={2.8} id="tt_r"/>
                <text x={ox+rx/2+8} y={oy+ry/2+18} fontSize="17" fill="#c0392b" fontWeight="700">R</text>
                <text x={154} y={H-56} textAnchor="middle" fontSize="15" fill="#555">Place tail of b at head of a.</text>
                <text x={154} y={H-38} textAnchor="middle" fontSize="15" fill="#555">R goes from tail of a to head of b.</text>
              </g>
            );
          })()}

          {/* ── Panel 2: Parallelogram method ── */}
          <rect x={308} y={10} width={273} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={445} y={28} textAnchor="middle" fontSize="17" fill="#1F3864" fontWeight="700">Parallelogram Method</text>
          <text x={445} y={44} textAnchor="middle" fontSize="16" fill="#888">same result, different layout</text>
          {(() => {
            const ox=332, oy=165;
            const ax=166, ay=-22, bx=73, by=-78;
            const rx=ax+bx, ry=ay+by;
            return (
              <g>
                <line x1={ox+ax} y1={oy+ay} x2={ox+rx} y2={oy+ry} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="4,3"/>
                <line x1={ox+bx} y1={oy+by} x2={ox+rx} y2={oy+ry} stroke="#2E75B6" strokeWidth="1.2" strokeDasharray="4,3"/>
                <polygon points={ox+","+oy+" "+(ox+ax)+","+(oy+ay)+" "+(ox+rx)+","+(oy+ry)+" "+(ox+bx)+","+(oy+by)}
                  fill="#EBF3FB" fillOpacity="0.6" stroke="none"/>
                <Arrow x1={ox} y1={oy} x2={ox+ax-4} y2={oy+ay+2} color="#2E75B6" id="pg_a"/>
                <text x={ox+ax/2} y={oy+ay/2+18} textAnchor="middle" fontSize="17" fill="#2E75B6" fontWeight="700">a</text>
                <Arrow x1={ox} y1={oy} x2={ox+bx-2} y2={oy+by+5} color="#27ae60" id="pg_b"/>
                <text x={ox+bx/2-16} y={oy+by/2+4} fontSize="17" fill="#27ae60" fontWeight="700">b</text>
                <Arrow x1={ox} y1={oy} x2={ox+rx-4} y2={oy+ry+4} color="#c0392b" width={2.8} id="pg_r"/>
                <text x={ox+rx/2+6} y={oy+ry/2-4} fontSize="17" fill="#c0392b" fontWeight="700">R</text>
                <text x={445} y={H-56} textAnchor="middle" fontSize="15" fill="#555">Both vectors from same origin.</text>
                <text x={445} y={H-38} textAnchor="middle" fontSize="15" fill="#555">R is the diagonal.</text>
              </g>
            );
          })()}

          {/* ── Panel 3: Subtraction ── */}
          <rect x={599} y={10} width={287} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={742} y={28} textAnchor="middle" fontSize="17" fill="#1F3864" fontWeight="700">Subtraction  a − b</text>
          <text x={742} y={44} textAnchor="middle" fontSize="16" fill="#888">= a + (−b)</text>
          {(() => {
            const ox=627, oy=155;
            const ax=166, ay=-18;
            const bx=62, by=-68;
            const nbx=-bx, nby=-by;
            const rx=ax+nbx, ry=ay+nby;
            return (
              <g>
                <Arrow x1={ox} y1={oy} x2={ox+ax-4} y2={oy+ay+1} color="#2E75B6" id="sub_a"/>
                <text x={ox+ax/2} y={oy+ay/2+16} textAnchor="middle" fontSize="17" fill="#2E75B6" fontWeight="700">a</text>
                <Arrow x1={ox} y1={oy} x2={ox+bx-2} y2={oy+by+4} color="#27ae60" width={1.4} id="sub_b"/>
                <text x={ox+bx/2-14} y={oy+by/2} fontSize="17" fill="#27ae60">b</text>
                <Arrow x1={ox+ax} y1={oy+ay} x2={ox+ax+nbx-2} y2={oy+ay+nby-4} color="#9b59b6" id="sub_nb"/>
                <text x={ox+ax+nbx/2+8} y={oy+ay+nby/2+4} fontSize="17" fill="#9b59b6" fontWeight="700">−b</text>
                <Arrow x1={ox} y1={oy} x2={ox+rx-3} y2={oy+ry+4} color="#c0392b" width={2.8} id="sub_r"/>
                <text x={ox+rx/2-22} y={oy+ry/2-8} fontSize="17" fill="#c0392b" fontWeight="700">a−b</text>
                <text x={742} y={H-56} textAnchor="middle" fontSize="15" fill="#555">Negate b, add tip-to-tail.</text>
                <text x={742} y={H-38} textAnchor="middle" fontSize="15" fill="#555">a−b points from tip of b to tip of a.</text>
              </g>
            );
          })()}
        </svg>
      </div>
      <div style={S.dCaption}>
        Addition: place the tail of b at the head of a — the resultant R goes from the tail of a to the head of b. Same result using the parallelogram diagonal. Subtraction a−b = a+(−b): negate b (reverse its arrow) then add tip-to-tail.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Dot Product
// ─────────────────────────────────────────────────────────────────
function DiagramDotProduct() {
  const W = 900, H = 370;
  const ox = 66, oy = 215, sc = 87;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  const a = [3, 0.8], b = [1, 2.6];
  const aMag = Math.sqrt(a[0]**2+a[1]**2);
  const bMag = Math.sqrt(b[0]**2+b[1]**2);
  const dot   = a[0]*b[0] + a[1]*b[1];
  const cosT  = dot / (aMag * bMag);
  const theta = Math.acos(cosT) * 180 / Math.PI;

  // Projection of b onto a
  const projLen = dot / aMag;
  const aUnit   = [a[0]/aMag, a[1]/aMag];
  const projPt  = [aUnit[0]*projLen, aUnit[1]*projLen];

  // Right panel starts at x=265
  const px0 = 464;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Dot Product — Projection, Angle Between Vectors, and Sign Interpretation</div>
      <div style={{ textAlign:'center' }}>
        <svg viewBox={"0 0 "+W+" "+H} style={{ width:'100%', display:'block' }}>

          {/* ── LEFT: Vector diagram ── */}
          {/* Grid lines */}
          {[0,1,2,3].map(v => (
            <g key={v}>
              <line x1={toX(v)} y1={10} x2={toX(v)} y2={oy+8} stroke="#eee" strokeWidth="1"/>
              <line x1={ox-8} y1={toY(v)} x2={toX(3.5)} y2={toY(v)} stroke="#eee" strokeWidth="1"/>
              <text x={toX(v)} y={oy+18} textAnchor="middle" fontSize="17" fill="#bbb">{v}</text>
              {v > 0 && <text x={ox-4} y={toY(v)+3} textAnchor="end" fontSize="17" fill="#bbb">{v}</text>}
            </g>
          ))}
          {/* Axes */}
          <line x1={ox-8} y1={oy} x2={toX(3.5)} y2={oy} stroke="#1F3864" strokeWidth="1.4"/>
          <line x1={ox} y1={oy+8} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.4"/>
          <text x={toX(3.5)+4} y={oy+4} fontSize="19" fill="#1F3864" fontWeight="700">x</text>
          <text x={ox+3} y={8} fontSize="19" fill="#1F3864" fontWeight="700">y</text>

          {/* Projection segment on a direction */}
          <line x1={toX(0)} y1={toY(0)} x2={toX(projPt[0])} y2={toY(projPt[1])}
            stroke="#e07b00" strokeWidth="4" strokeLinecap="round" opacity="0.55"/>

          {/* Projection drop line (dashed) */}
          <line x1={toX(b[0])} y1={toY(b[1])} x2={toX(projPt[0])} y2={toY(projPt[1])}
            stroke="#e07b00" strokeWidth="1.5" strokeDasharray="5,3"/>

          {/* Right angle at foot of projection */}
          {(() => {
            const footX = toX(projPt[0]), footY = toY(projPt[1]);
            const aAngle = Math.atan2(-a[1], a[0]);
            const s = 9;
            const px = footX + s*Math.cos(aAngle+Math.PI/2);
            const py = footY + s*Math.sin(aAngle+Math.PI/2);
            const qx = footX + s*Math.cos(aAngle);
            const qy = footY + s*Math.sin(aAngle);
            return <polyline points={(px+qx-footX)+","+(py+qy-footY)+" "+px+","+py+" "+footX+","+footY+" "+qx+","+qy}
              fill="none" stroke="#e07b00" strokeWidth="1.2"/>;
          })()}

          {/* Projection label — below the orange segment */}
          <text x={toX(projPt[0]/2)+6} y={toY(projPt[1]/2)+20} fontSize="9.5" fill="#e07b00" fontWeight="700">
            {"proj = "+projLen.toFixed(2)}
          </text>

          {/* Vector a */}
          <Arrow x1={toX(0)} y1={toY(0)} x2={toX(a[0])-4} y2={toY(a[1])+2} color="#2E75B6" width={2.8} id="dot_a"/>
          <text x={toX(a[0])+7} y={toY(a[1])+5} fontSize="23" fill="#2E75B6" fontWeight="700">a</text>

          {/* Vector b */}
          <Arrow x1={toX(0)} y1={toY(0)} x2={toX(b[0])-2} y2={toY(b[1])+4} color="#c0392b" width={2.8} id="dot_b"/>
          <text x={toX(b[0])-16} y={toY(b[1])-7} fontSize="23" fill="#c0392b" fontWeight="700">b</text>

          {/* Angle arc + theta label at triangle centroid */}
          {(() => {
            const r = 42;
            const tA = Math.atan2(a[1], a[0]);
            const tB = Math.atan2(b[1], b[0]);
            // Centroid of triangle origin / tip-a / tip-b
            const cx = toX((0 + a[0] + b[0]) / 3);
            const cy = toY((0 + a[1] + b[1]) / 3);
            return (
              <g>
                <path d={"M "+(ox+r*Math.cos(tA))+","+(oy-r*Math.sin(tA))+" A "+r+" "+r+" 0 0 0 "+(ox+r*Math.cos(tB))+","+(oy-r*Math.sin(tB))}
                  fill="none" stroke="#9b59b6" strokeWidth="1.8"/>
                <text x={cx} y={cy} textAnchor="middle" fontSize="17" fill="#9b59b6" fontWeight="700">{"θ="+theta.toFixed(1)+"°"}</text>
              </g>
            );
          })()}

          {/* ── RIGHT: Formula panel ── */}
          <rect x={px0} y={10} width={W-px0-8} height={232} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={px0+(W-px0-8)/2} y={27} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Dot Product Formulas</text>

          {/* Algebraic */}
          <rect x={px0+6} y={33} width={W-px0-20} height={42} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={px0+12} y={50} fontSize="14" fill="#1F3864" fontWeight="700">Algebraic:  a·b = ax·bx + ay·by + az·bz</text>
          <text x={px0+12} y={67} fontSize="14" fill="#2E75B6" fontFamily="'Courier New',monospace">
            {"= "+a[0]+"×"+b[0]+" + "+a[1]+"×"+b[1]+" = "+dot.toFixed(2)}
          </text>

          {/* Geometric */}
          <rect x={px0+6} y={81} width={W-px0-20} height={42} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
          <text x={px0+12} y={98} fontSize="14" fill="#1F3864" fontWeight="700">Geometric:  a·b = |a||b| cos θ</text>
          <text x={px0+12} y={115} fontSize="14" fill="#27ae60" fontFamily="'Courier New',monospace">
            {"= "+aMag.toFixed(2)+"×"+bMag.toFixed(2)+"×cos("+theta.toFixed(1)+"°) = "+dot.toFixed(2)}
          </text>

          {/* Angle */}
          <text x={px0+12} y={136} fontSize="14" fill="#1F3864" fontWeight="700">Finding the angle:</text>
          <text x={px0+12} y={152} fontSize="14" fill="#9b59b6" fontFamily="'Courier New',monospace">{"cos θ = a·b / (|a||b|)"}</text>
          <text x={px0+12} y={167} fontSize="14" fill="#9b59b6" fontFamily="'Courier New',monospace">{"θ = arccos("+cosT.toFixed(3)+") = "+theta.toFixed(1)+"°"}</text>

          {/* Projection */}
          <text x={px0+12} y={185} fontSize="14" fill="#1F3864" fontWeight="700">Scalar projection of b onto a:</text>
          <text x={px0+12} y={201} fontSize="14" fill="#e07b00" fontFamily="'Courier New',monospace">{"proj = a·b / |a| = "+projLen.toFixed(2)}</text>

          {/* Perpendicular test */}
          <rect x={px0+6} y={209} width={W-px0-20} height={28} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
          <text x={px0+12} y={221} fontSize="13" fill="#1F3864" fontWeight="700">Perpendicularity test:</text>
          <text x={px0+12} y={233} fontSize="13" fill="#e07b00" fontFamily="'Courier New',monospace">a·b = 0  ↔  vectors are perpendicular</text>

          {/* ── BOTTOM STRIP: Sign cases ── */}
          <rect x={14} y={250} width={W-28} height={100} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={W/2} y={268} textAnchor="middle" fontSize="17" fill="#1F3864" fontWeight="700">Sign Interpretation</text>
          {[
            { x:20,   color:"#27ae60", title:"a·b > 0", l1:"angle < 90° — same general direction,", l2:"components reinforce each other" },
            { x:320,  color:"#9b59b6", title:"a·b = 0", l1:"angle = 90° — perpendicular vectors,", l2:"no component in common" },
            { x:620,  color:"#c0392b", title:"a·b < 0", l1:"angle > 90° — opposing directions,", l2:"components cancel each other" },
          ].map(({ x, color, title, l1, l2 }) => (
            <g key={title}>
              <text x={x+6} y={286} fontSize="16" fill={color} fontWeight="700">{title}</text>
              <text x={x+6} y={303} fontSize="14" fill="#555">{l1}</text>
              <text x={x+6} y={319} fontSize="14" fill="#555">{l2}</text>
            </g>
          ))}
          <line x1={314} y1={252} x2={314} y2={348} stroke="#eee" strokeWidth="1"/>
          <line x1={614} y1={252} x2={614} y2={348} stroke="#eee" strokeWidth="1"/>
        </svg>
      </div>
      <div style={S.dCaption}>
        The dot product produces a scalar. Geometrically it equals the length of the projection of b onto a, multiplied by |a|. Use it to find angles between vectors, check perpendicularity (dot = 0), and compute work (F·d).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Cross Product
// ─────────────────────────────────────────────────────────────────
function DiagramCrossProduct() {
  const W = 900, H = 380;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cross Product — Right-Hand Rule, Perpendicular Result, and Magnitude as Area</div>
      <div style={{ textAlign:'center' }}>
        <svg viewBox={"0 0 "+W+" "+H} style={{ width:'100%', display:'block' }}>

          {/* ── Panel 1: Geometric meaning x=10..299 ── */}
          <rect x={10} y={10} width={289} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={154} y={28} textAnchor="middle" fontSize="16" fill="#1F3864" fontWeight="700">Geometric Meaning</text>
          {(() => {
            // Shrink vectors so parallelogram fits: ox=22, ax=150, bx=70 → right edge=22+150+70=242 < 299 ✓
            const ox=22, oy=195;
            const ax=150, ay=-14, bx=70, by=-72;
            const rx=ax+bx, ry=ay+by;
            return (
              <g>
                <polygon points={ox+","+oy+" "+(ox+ax)+","+(oy+ay)+" "+(ox+rx)+","+(oy+ry)+" "+(ox+bx)+","+(oy+by)}
                  fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
                <text x={ox+rx/2} y={oy+ry/2+18} textAnchor="middle" fontSize="14" fill="#2E75B6" fontStyle="italic">area = |a×b|</text>
                <Arrow x1={ox} y1={oy} x2={ox+ax-4} y2={oy+ay+1} color="#2E75B6" id="cp_a"/>
                <text x={ox+ax/2} y={oy+ay/2+14} textAnchor="middle" fontSize="15" fill="#2E75B6" fontWeight="700">a</text>
                <Arrow x1={ox} y1={oy} x2={ox+bx-2} y2={oy+by+5} color="#27ae60" id="cp_b"/>
                <text x={ox+bx/2-14} y={oy+by/2} fontSize="15" fill="#27ae60" fontWeight="700">b</text>
                {/* a×b arrow emerges from parallelogram surface */}
                <Arrow x1={ox+38} y1={oy-60} x2={ox+38} y2={oy-100} color="#c0392b" id="cp_axb"/>
                <circle cx={ox+38} cy={oy-60} r={4} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
                <text x={ox+48} y={oy-102} fontSize="14" fill="#c0392b" fontWeight="700">a×b</text>
                <text x={ox+48} y={oy-88} fontSize="13" fill="#c0392b">(out of plane)</text>
                {/* Bottom text — 16px spacing */}
                <text x={154} y={H-52} textAnchor="middle" fontSize="14" fill="#555">|a×b| = |a||b|sinθ</text>
                <text x={154} y={H-34} textAnchor="middle" fontSize="14" fill="#555">= area of parallelogram formed by a and b</text>
              </g>
            );
          })()}

          {/* ── Panel 2: Right-hand rule x=305..577 ── */}
          <rect x={305} y={10} width={272} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={441} y={28} textAnchor="middle" fontSize="16" fill="#1F3864" fontWeight="700">Right-Hand Rule</text>
          {(() => {
            const hx=350, hy=55;
            return (
              <g>
                {/* Palm */}
                <rect x={hx} y={hy+60} width={120} height={32} rx={6} fill="#ffe0cc" stroke="#e07b00" strokeWidth="1.5"/>
                {/* Four fingers */}
                {[0,1,2,3].map(i => (
                  <rect key={i} x={hx+4+i*17} y={hy+10} width={14} height={52} rx={5} fill="#ffe0cc" stroke="#e07b00" strokeWidth="1.2"/>
                ))}
                {/* Thumb */}
                <rect x={hx+72} y={hy+68} width={50} height={11} rx={5} fill="#ffe0cc" stroke="#e07b00" strokeWidth="1.2"/>
                {/* Arrow labels — positioned to right of hand */}
                <Arrow x1={441} y1={hy+92} x2={500} y2={hy+92} color="#2E75B6" id="rhr_a"/>
                <text x={505} y={hy+97} fontSize="14" fill="#2E75B6" fontWeight="700">a (fingers)</text>
                <Arrow x1={441} y1={hy+92} x2={441} y2={hy+64} color="#27ae60" id="rhr_b"/>
                <text x={450} y={hy+60} fontSize="14" fill="#27ae60" fontWeight="700">b (curl)</text>
                <Arrow x1={441} y1={hy+60} x2={441} y2={hy+20} color="#c0392b" width={3} id="rhr_axb"/>
                <text x={450} y={hy+16} fontSize="14" fill="#c0392b" fontWeight="700">a×b (thumb)</text>
                {/* Instructions — 16px line spacing */}
                <text x={441} y={H-74} textAnchor="middle" fontSize="14" fill="#555">Point fingers along a.</text>
                <text x={441} y={H-56} textAnchor="middle" fontSize="14" fill="#555">Curl them toward b.</text>
                <text x={441} y={H-38} textAnchor="middle" fontSize="14" fill="#555">Thumb = direction of a×b.</text>
                <text x={441} y={H-18} textAnchor="middle" fontSize="14" fill="#c0392b" fontWeight="700">Note: b×a = −(a×b)</text>
              </g>
            );
          })()}

          {/* ── Panel 3: Determinant form x=583..886 ── */}
          <rect x={583} y={10} width={303} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={734} y={28} textAnchor="middle" fontSize="16" fill="#1F3864" fontWeight="700">Determinant Form</text>
          {/* Matrix rows — 16px spacing */}
          {[
            ["a×b =", "#888"],
            ["| i    j    k  |", "#1F3864"],
            ["| ax  ay  az |", "#2E75B6"],
            ["| bx  by  bz |", "#27ae60"],
          ].map(([line, col], i) => (
            <text key={i} x={596} y={48+i*18} fontSize="14" fill={col} fontFamily="'Courier New',monospace">{line}</text>
          ))}

          {/* Expanding box */}
          <rect x={591} y={124} width={287} height={108} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={734} y={139} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Expanding the determinant:</text>
          <text x={599} y={155} fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace">i·(ay·bz − az·by)</text>
          <text x={599} y={171} fontSize="13" fill="#c0392b" fontFamily="'Courier New',monospace">−j·(ax·bz − az·bx)</text>
          <text x={599} y={187} fontSize="13" fill="#27ae60" fontFamily="'Courier New',monospace">+k·(ax·by − ay·bx)</text>
          <text x={599} y={203} fontSize="13" fill="#e07b00" fontFamily="'Courier New',monospace">2D: |a×b| = ax·by − ay·bx</text>

          {/* Key properties — 16px spacing */}
          {[
            ["Only defined in 3D (or 7D)", "#555"],
            ["Result is a vector (not scalar)", "#555"],
            ["Perpendicular to both a and b", "#555"],
            ["Zero if a ∥ b  (sinθ = 0)", "#555"],
          ].map(([txt, col], i) => (
            <text key={i} x={596} y={244+i*18} fontSize="13" fill={col} fontFamily="'Courier New',monospace">{txt}</text>
          ))}
        </svg>
      </div>
      <div style={S.dCaption}>
        The cross product produces a vector perpendicular to both inputs. Its magnitude equals the area of the parallelogram formed by a and b. The right-hand rule gives the direction. Order matters: b×a = -(a×b).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Unit Vectors and Basis
// ─────────────────────────────────────────────────────────────────
function DiagramBasis() {
  // Graph: ox=90, sc=52, oy=185. Grid -1..3 → top y=toY(3)=185-3*52=29, bottom=oy+22=207
  // Right panel: 4 boxes, each ~50px tall, total ~270px. H = max(207, 270)+20 = 290
  const W = 900, H = 290;
  const ox = 90, oy = 185, sc = 52;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  const vx = 2.5, vy = 2;
  const mag = Math.sqrt(vx*vx+vy*vy);

  // Right panel: x=470, width=422 → right edge=892 ✓
  const rpx = 470, rpw = 422, rpmid = rpx + rpw/2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Unit Vectors and Basis — i, j, k Components and Normalisation</div>
      <div style={{ textAlign:'center' }}>
        <svg viewBox={"0 0 "+W+" "+H} style={{ width:'100%', display:'block' }}>
          {/* Grid */}
          {[-1,0,1,2,3].map(v => (
            <g key={v}>
              <line x1={toX(v)} y1={20} x2={toX(v)} y2={oy+10} stroke="#eee" strokeWidth="1"/>
              <line x1={ox-sc-8} y1={toY(v)} x2={toX(3.2)} y2={toY(v)} stroke="#eee" strokeWidth="1"/>
              <text x={toX(v)} y={oy+16} textAnchor="middle" fontSize="14" fill="#bbb">{v}</text>
              <text x={ox-sc-5} y={toY(v)+4} textAnchor="end" fontSize="14" fill="#bbb">{v}</text>
            </g>
          ))}
          {/* Axes */}
          <line x1={ox-sc-8} y1={oy} x2={toX(3.2)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox} y1={oy+10} x2={ox} y2={20} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={toX(3.2)+5} y={oy+4} fontSize="15" fill="#1F3864" fontWeight="700">x</text>
          <text x={ox+4} y={18} fontSize="15" fill="#1F3864" fontWeight="700">y</text>

          {/* i unit vector */}
          <Arrow x1={toX(0)} y1={oy} x2={toX(1)-5} y2={oy} color="#2E75B6" width={2.5} id="bx_i"/>
          <text x={toX(0.5)} y={oy-9} textAnchor="middle" fontSize="14" fill="#2E75B6" fontWeight="700">i</text>
          <text x={toX(0.5)} y={oy+16} textAnchor="middle" fontSize="13" fill="#2E75B6">|i|=1</text>

          {/* j unit vector */}
          <Arrow x1={ox} y1={oy} x2={ox} y2={toY(1)+5} color="#c0392b" width={2.5} id="bx_j"/>
          <text x={ox-12} y={toY(0.5)+2} fontSize="14" fill="#c0392b" fontWeight="700">j</text>
          <text x={ox-30} y={toY(0.5)+14} fontSize="13" fill="#c0392b">|j|=1</text>

          {/* vx component */}
          <line x1={toX(0)} y1={oy} x2={toX(vx)} y2={oy} stroke="#2E75B6" strokeWidth="2" strokeDasharray="5,3" opacity="0.6"/>
          <text x={toX(vx/2)} y={oy+22} textAnchor="middle" fontSize="14" fill="#2E75B6">{"vx·i = "+vx+"i"}</text>

          {/* vy component */}
          <line x1={toX(vx)} y1={oy} x2={toX(vx)} y2={toY(vy)} stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3" opacity="0.6"/>
          <text x={toX(vx)+30} y={toY(vy/2)+4} fontSize="14" fill="#c0392b">{"vy·j = "+vy+"j"}</text>

          {/* Main vector v */}
          <Arrow x1={toX(0)} y1={oy} x2={toX(vx)-4} y2={toY(vy)+3} color="#1F3864" width={3} id="bx_v"/>
          <text x={toX(vx)+7} y={toY(vy)-5} fontSize="15" fill="#1F3864" fontWeight="700">v</text>

          {/* Unit vector */}
          <Arrow x1={toX(0)} y1={oy} x2={toX(vx/mag)-4} y2={toY(vy/mag)+3} color="#e07b00" width={2.2} id="bx_uv"/>
          <text x={toX(vx/mag)+7} y={toY(vy/mag)+4} fontSize="14" fill="#e07b00" fontWeight="700">v̂ (unit)</text>

          {/* ── RIGHT PANEL: x=470, width=422, right edge=892 ── */}
          <rect x={rpx} y={10} width={rpw} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={rpmid} y={26} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Basis Vectors and Decomposition</text>

          {/* Box 1 — Standard Basis */}
          <rect x={rpx+6} y={32} width={rpw-12} height={58} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={rpmid} y={46} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Standard Basis (Cartesian)</text>
          <text x={rpx+12} y={60} fontSize="14" fill="#2E75B6" fontFamily="'Courier New',monospace">i = (1,0,0)  |i| = 1</text>
          <text x={rpx+12} y={75} fontSize="14" fill="#c0392b" fontFamily="'Courier New',monospace">j = (0,1,0)  |j| = 1</text>
          <text x={rpx+250} y={60} fontSize="14" fill="#27ae60" fontFamily="'Courier New',monospace">k = (0,0,1)  |k| = 1</text>

          {/* Box 2 — Component sum */}
          <rect x={rpx+6} y={96} width={rpw-12} height={46} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
          <text x={rpmid} y={110} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Any vector as component sum:</text>
          <text x={rpx+12} y={125} fontSize="14" fill="#1F3864" fontFamily="'Courier New',monospace">{"v = vx·i + vy·j + vz·k    e.g. v = "+vx+"i + "+vy+"j"}</text>

          {/* Box 3 — Normalising */}
          <rect x={rpx+6} y={148} width={rpw-12} height={60} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
          <text x={rpmid} y={162} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Normalising (making unit vector):</text>
          <text x={rpx+12} y={177} fontSize="14" fill="#27ae60" fontFamily="'Courier New',monospace">v̂ = v / |v|</text>
          <text x={rpx+12} y={194} fontSize="14" fill="#555" fontFamily="'Courier New',monospace">{"= ("+vx+","+vy+") / "+mag.toFixed(3)+" = ("+(vx/mag).toFixed(3)+","+(vy/mag).toFixed(3)+")"}</text>

          {/* Box 4 — Cross products */}
          <rect x={rpx+6} y={214} width={rpw-12} height={46} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
          <text x={rpmid} y={228} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Cross products of basis vectors:</text>
          <text x={rpx+12} y={248} fontSize="14" fill="#555" fontFamily="'Courier New',monospace">i×j=k   j×k=i   k×i=j      j×i=-k   k×j=-i   i×k=-j</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Any vector can be expressed as a sum of scaled unit basis vectors i, j, k. A unit vector has magnitude exactly 1 and indicates direction only. Normalising divides the vector by its own magnitude — the result always has length 1.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Engineering Applications
// ─────────────────────────────────────────────────────────────────
function DiagramApplications() {
  // Content: diagrams ~50-155, info boxes 160-285. H=300
  const W = 900, H = 300;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Vectors Across Engineering — Force Resolution, Work, Torque, and Fields</div>
      <div style={{ textAlign:'center' }}>
        <svg viewBox={"0 0 "+W+" "+H} style={{ width:'100%', display:'block' }}>

          {/* ── Panel 1: Force resolution x=10..229 ── */}
          <rect x={10} y={10} width={219} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={119} y={26} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Force Resolution</text>
          <text x={119} y={41} textAnchor="middle" fontSize="13" fill="#888">Statics / Structural</text>
          {(() => {
            // graphic center between y=41 and info box y=152 → midpoint=96, F=100 so oy~110
            const ox=30, oy=110, F=95, t=40*Math.PI/180;
            const Fx=F*Math.cos(t), Fy=F*Math.sin(t);
            return (
              <g>
                <Arrow x1={ox} y1={oy} x2={ox+Fx-3} y2={oy-Fy+2} color="#1F3864" width={2.5} id="app_F"/>
                <text x={ox+Fx+5} y={oy-Fy+2} fontSize="14" fill="#1F3864" fontWeight="700">F</text>
                <line x1={ox} y1={oy} x2={ox+Fx} y2={oy} stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="4,3"/>
                <line x1={ox+Fx} y1={oy} x2={ox+Fx} y2={oy-Fy} stroke="#c0392b" strokeWidth="1.8" strokeDasharray="4,3"/>
                <Arrow x1={ox} y1={oy} x2={ox+Fx-3} y2={oy} color="#2E75B6" id="app_Fx"/>
                <Arrow x1={ox} y1={oy} x2={ox} y2={oy-Fy+3} color="#c0392b" id="app_Fy"/>
                <text x={ox+Fx/2} y={oy+13} textAnchor="middle" fontSize="12" fill="#2E75B6">Fx=Fcosθ</text>
                <text x={ox-4} y={oy-Fy/2+2} textAnchor="end" fontSize="12" fill="#c0392b">Fy=Fsinθ</text>
                <path d={"M "+(ox+18)+","+oy+" A 18 18 0 0 0 "+(ox+18*Math.cos(t))+","+(oy-18*Math.sin(t))} fill="none" stroke="#e07b00" strokeWidth="1.4"/>
                <text x={ox+22} y={oy-10} fontSize="12" fill="#e07b00">θ</text>
                <rect x={16} y={152} width={203} height={118} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
                <text x={119} y={168} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">F = Fx·i + Fy·j</text>
                <text x={119} y={184} textAnchor="middle" fontSize="13" fill="#555">Sum forces for equilibrium:</text>
                <text x={119} y={200} textAnchor="middle" fontSize="13" fill="#555">ΣF = 0</text>
                <text x={119} y={216} textAnchor="middle" fontSize="13" fill="#555">Fx = F cosθ</text>
                <text x={119} y={232} textAnchor="middle" fontSize="13" fill="#555">Fy = F sinθ</text>
              </g>
            );
          })()}

          {/* ── Panel 2: Work x=237..456 ── */}
          <rect x={237} y={10} width={219} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={346} y={26} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Work = F·d</text>
          <text x={346} y={41} textAnchor="middle" fontSize="13" fill="#888">Mechanical / Civil</text>
          {(() => {
            // center graphic between y=41 and info box y=152 → oy≈100
            const ox=248, oy=100, F=88, t=30*Math.PI/180, d=148;
            const Fd=F*Math.cos(t);
            return (
              <g>
                <Arrow x1={ox} y1={oy} x2={ox+d-4} y2={oy} color="#27ae60" width={2.5} id="app_d"/>
                <text x={ox+d/2} y={oy+14} textAnchor="middle" fontSize="14" fill="#27ae60" fontWeight="700">d</text>
                <Arrow x1={ox} y1={oy} x2={ox+F*Math.cos(t)-3} y2={oy-F*Math.sin(t)+2} color="#2E75B6" width={2.5} id="app_W"/>
                <text x={ox+F*Math.cos(t)+5} y={oy-F*Math.sin(t)+2} fontSize="14" fill="#2E75B6" fontWeight="700">F</text>
                <line x1={ox} y1={oy} x2={ox+Fd} y2={oy} stroke="#e07b00" strokeWidth="3" strokeLinecap="round" opacity="0.5"/>
                <text x={ox+Fd/2} y={oy-8} textAnchor="middle" fontSize="12" fill="#e07b00">F·cosθ</text>
                <path d={"M "+(ox+18)+","+oy+" A 18 18 0 0 0 "+(ox+18*Math.cos(t))+","+(oy-18*Math.sin(t))} fill="none" stroke="#e07b00" strokeWidth="1.4"/>
                <text x={ox+22} y={oy-7} fontSize="12" fill="#e07b00">θ</text>
                <rect x={243} y={152} width={207} height={118} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
                <text x={346} y={168} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">W = F·d = |F||d|cosθ</text>
                <text x={346} y={184} textAnchor="middle" fontSize="13" fill="#555">Only force component</text>
                <text x={346} y={200} textAnchor="middle" fontSize="13" fill="#555">along displacement</text>
                <text x={346} y={216} textAnchor="middle" fontSize="13" fill="#555">does work.</text>
                <text x={346} y={232} textAnchor="middle" fontSize="13" fill="#555">W = 0 when F ⊥ d</text>
              </g>
            );
          })()}

          {/* ── Panel 3: Torque x=464..683 ── */}
          <rect x={464} y={10} width={219} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={573} y={26} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Torque = r×F</text>
          <text x={573} y={41} textAnchor="middle" fontSize="13" fill="#888">Mechanical / Aerospace</text>
          {(() => {
            // center graphic between y=41 and y=152; pivot oy≈105, r=58
            const ox=476, oy=105, r=58, t=50*Math.PI/180;
            const px=ox+r*Math.cos(t), py=oy-r*Math.sin(t);
            const Fx=-Math.sin(t)*50, Fy=-Math.cos(t)*50;
            return (
              <g>
                <circle cx={ox} cy={oy} r={6} fill="#1F3864"/>
                <text x={ox} y={oy+16} textAnchor="middle" fontSize="12" fill="#1F3864">pivot</text>
                <line x1={ox} y1={oy} x2={px} y2={py} stroke="#2E75B6" strokeWidth="2.2"/>
                <text x={(ox+px)/2+5} y={(oy+py)/2+11} fontSize="14" fill="#2E75B6" fontWeight="700">r</text>
                <Arrow x1={px} y1={py} x2={px+Fx-3} y2={py+Fy+3} color="#c0392b" width={2.5} id="app_T"/>
                <text x={px+Fx+5} y={py+Fy+2} fontSize="14" fill="#c0392b" fontWeight="700">F</text>
                {(() => {
                  const rLen=10;
                  const ru=[Math.cos(t),-Math.sin(t)], fu=[-Math.sin(t),-Math.cos(t)];
                  const p1=[px+ru[0]*rLen,py+ru[1]*rLen], p2=[px+ru[0]*rLen+fu[0]*rLen,py+ru[1]*rLen+fu[1]*rLen], p3=[px+fu[0]*rLen,py+fu[1]*rLen];
                  return <polyline points={p1.join(',')+' '+p2.join(',')+' '+p3.join(',')} fill="none" stroke="#888" strokeWidth="1.2"/>;
                })()}
                <path d={"M "+(ox+22)+","+oy+" A 22 22 0 0 0 "+(ox+22*Math.cos(t+0.5))+","+(oy-22*Math.sin(t+0.5))} fill="none" stroke="#e07b00" strokeWidth="1.8" strokeDasharray="4,2"/>
                <rect x={470} y={152} width={207} height={118} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
                <text x={573} y={168} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">T = r × F</text>
                <text x={573} y={184} textAnchor="middle" fontSize="13" fill="#555">|T| = |r||F|sinθ</text>
                <text x={573} y={200} textAnchor="middle" fontSize="13" fill="#555">Maximum when r ⊥ F</text>
                <text x={573} y={216} textAnchor="middle" fontSize="13" fill="#555">Direction: right-hand rule</text>
                <text x={573} y={232} textAnchor="middle" fontSize="13" fill="#555">Zero when r ∥ F</text>
              </g>
            );
          })()}

          {/* ── Panel 4: Normal vectors x=691..890 ── */}
          <rect x={691} y={10} width={199} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={790} y={26} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Normal Vectors</text>
          <text x={790} y={41} textAnchor="middle" fontSize="13" fill="#888">Fluids / Electrical</text>
          {(() => {
            // center surface between y=41 and y=152 → sy≈110
            const sx=705, sy=112, sw=104, sh=13, off=16;
            const surf=[[sx,sy],[sx+sw,sy],[sx+sw-off,sy-sh],[sx-off,sy-sh]];
            const ncx=sx+sw/2-off/2, ncy=sy-sh/2;
            return (
              <g>
                <polygon points={surf.map(p=>p.join(',')).join(' ')} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="1.5"/>
                <text x={ncx} y={sy+13} textAnchor="middle" fontSize="13" fill="#2E75B6">surface</text>
                <Arrow x1={ncx} y1={ncy} x2={ncx} y2={ncy-46} color="#c0392b" width={2.5} id="app_n"/>
                <text x={ncx+7} y={ncy-48} fontSize="14" fill="#c0392b" fontWeight="700">n̂</text>
                {[-22,-7,7,22].map((dx,i) => (
                  <Arrow key={i} x1={ncx+dx} y1={sy-sh-38} x2={ncx+dx} y2={sy-sh-12} color="#e07b00" width={1.3} id={"app_fl"+i}/>
                ))}
                <rect x={697} y={152} width={187} height={118} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
                <text x={790} y={168} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Flux = F·n̂·dA</text>
                <text x={790} y={184} textAnchor="middle" fontSize="13" fill="#555">Normal = cross product</text>
                <text x={790} y={200} textAnchor="middle" fontSize="13" fill="#555">of two surface tangents</text>
                <text x={790} y={218} textAnchor="middle" fontSize="14" fill="#9b59b6" fontWeight="700">n̂ = (a×b)/|a×b|</text>
              </g>
            );
          })()}
        </svg>
      </div>
      <div style={S.dCaption}>
        Four fundamental engineering uses. Force resolution uses component form. Work uses the dot product — only the force component along displacement contributes. Torque uses the cross product — magnitude r·F·sinθ, direction from the right-hand rule. Surface normals use the cross product of two tangent vectors, essential for flux calculations in fluids and electromagnetics.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Vectors(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Vectors</h1>

      <p style={S.p}>
        A vector is a quantity that has both magnitude and direction. Unlike a scalar (which has magnitude only), a vector tells you not just how much but also which way. Forces, velocities, displacements, electric fields, and moments are all vectors. Every branch of engineering uses vectors constantly.
      </p>

      <InfoBox
        title="Vector Notation:  v = (vx, vy, vz) = vx·i + vy·j + vz·k"
        items={[
          "Magnitude: |v| = sqrt(vx² + vy² + vz²) — the length of the arrow",
          "Direction: unit vector v̂ = v / |v| — magnitude 1, same direction as v",
          "Equality: two vectors are equal if and only if all corresponding components are equal",
          "Vectors are free — they can be moved anywhere as long as magnitude and direction are preserved",
        ]}
      />

      <h2 style={S.h2}>Vector Anatomy</h2>
      <DiagramAnatomy />
      <Table2 rows={[
        ["Component form",   "v = (vx, vy) in 2D  or  v = (vx, vy, vz) in 3D\nvx = |v|cosθ,  vy = |v|sinθ"],
        ["Magnitude",        "|v| = sqrt(vx²+vy²+vz²)\nAlways non-negative — the length of the vector"],
        ["Direction angle",  "θ = arctan(vy/vx)  in 2D\nUse atan2(vy,vx) in code to get correct quadrant"],
        ["Unit vector",      "v̂ = v/|v| — same direction, length exactly 1\nUsed to specify direction independently of magnitude"],
        ["Zero vector",      "0 = (0,0,0) — magnitude 0, no defined direction\nIdentity element for vector addition"],
        ["Negative vector",  "-v = (-vx,-vy,-vz) — same magnitude, opposite direction\nAdding v + (-v) = 0"],
      ]} />

      <h2 style={S.h2}>Addition and Subtraction</h2>
      <DiagramAddSub />
      <Table2 rows={[
        ["Addition",        "a + b = (ax+bx, ay+by, az+bz)\nAdd component-by-component"],
        ["Tip-to-tail",     "Place tail of b at head of a — resultant goes from tail of a to head of b\nOrder does not matter: a+b = b+a (commutative)"],
        ["Parallelogram",   "Draw both from same origin — resultant is the diagonal\nSame result as tip-to-tail, different visual layout"],
        ["Subtraction",     "a - b = a + (-b) = (ax-bx, ay-by, az-bz)\nGeometrically: vector from tip of b to tip of a (both from same origin)"],
        ["Scalar multiply", "c·v = (c·vx, c·vy, c·vz)\nScales magnitude by |c|, reverses direction if c < 0"],
        ["Properties",      "Commutative: a+b = b+a\nAssociative: (a+b)+c = a+(b+c)\nDistributive: c(a+b) = ca+cb"],
      ]} />

      <h2 style={S.h2}>Dot Product</h2>
      <DiagramDotProduct />
      <Table2 rows={[
        ["Definition",      "a·b = ax·bx + ay·by + az·bz  (scalar result)\na·b = |a||b|cosθ  (geometric form)"],
        ["Finding angle",   "cosθ = a·b / (|a||b|)\nθ = arccos(a·b / (|a||b|))"],
        ["Perpendicular",   "a·b = 0  ↔  vectors are perpendicular (θ = 90°)\nKey test in structural analysis and kinematics"],
        ["Projection",      "proj of b onto a = (a·b)/|a|  (scalar)\nvector projection = ((a·b)/|a|²)·a"],
        ["Properties",      "Commutative: a·b = b·a\nDistributive: a·(b+c) = a·b + a·c\nNOT associative: (a·b)·c is not defined the same way"],
        ["Work",            "W = F·d = |F||d|cosθ\nOnly the component of force along displacement does work"],
      ]} />

      <h2 style={S.h2}>Cross Product</h2>
      <DiagramCrossProduct />
      <Table2 rows={[
        ["Definition",      "a×b = (ay·bz-az·by, az·bx-ax·bz, ax·by-ay·bx)\nResult is a VECTOR perpendicular to both a and b"],
        ["Magnitude",       "|a×b| = |a||b|sinθ\n= area of the parallelogram formed by a and b"],
        ["Direction",       "Right-hand rule: point fingers along a, curl toward b, thumb = a×b direction\nReversing order reverses direction: b×a = -(a×b)"],
        ["Anti-commutative","a×b = -(b×a)\nOrder matters — unlike the dot product"],
        ["Parallel vectors","a×b = 0 when vectors are parallel (sinθ = 0)\nUsed to test collinearity"],
        ["Torque",          "T = r×F — moment arm crossed with force\n|T| = |r||F|sinθ, maximum when r perpendicular to F"],
        ["Normal vector",   "n = a×b gives a vector perpendicular to the plane of a and b\nNormalise: n̂ = (a×b)/|a×b|"],
      ]} />

      <h2 style={S.h2}>Unit Vectors and Basis</h2>
      <DiagramBasis />
      <Table2 rows={[
        ["Standard basis",  "i = (1,0,0),  j = (0,1,0),  k = (0,0,1)\nOrthonormal: unit length and mutually perpendicular"],
        ["Decomposition",   "Any vector v = vx·i + vy·j + vz·k\nComponents are projections onto each axis: vx = v·i"],
        ["Normalising",     "v̂ = v/|v|  — always length 1\nUsed when only direction matters, not magnitude"],
        ["Basis cross products","i×j = k,  j×k = i,  k×i = j\nj×i = -k,  k×j = -i,  i×k = -j"],
        ["Orthogonality",   "i·j = 0,  j·k = 0,  i·k = 0\ni·i = 1,  j·j = 1,  k·k = 1"],
        ["Non-standard basis","Any three mutually orthogonal unit vectors form a valid basis\nUsed in local coordinate systems, body frames, principal axes"],
      ]} />

      <h2 style={S.h2}>Engineering Applications</h2>
      <DiagramApplications />

      <InfoBox
        title="Key Takeaway — Which Vector Operation to Use and When"
        items={[
          "Need to combine forces or displacements? → Vector addition: R = F₁ + F₂ + F₃",
          "Need the angle between two vectors? → Dot product: cosθ = a·b / (|a||b|)",
          "Need to check if two vectors are perpendicular? → Dot product: a·b = 0",
          "Need the component of a force along a direction? → Dot product projection: (F·d̂)",
          "Need torque, moment, or a vector perpendicular to a surface? → Cross product: T = r×F",
          "Need to find a surface normal for flux or pressure calculations? → Cross product: n̂ = (a×b)/|a×b|",
          "Work done by a force: W = F·d  (dot product — scalar result)",
        ]}
      />
    </div>
  );
}